// DÙNG ĐỂ LƯU TRẠNG THÁI USER FREE / BASIC / PRO
import { create } from "zustand";
import axios from "axios";
import { storage } from "../utils/storage";
import { TierName } from "../types/AIModel";

const baseUrl = process.env.REACT_APP_API_CONFIG;

export type SubscriptionPlatform = 'apple' | 'google' | 'stripe' | 'paddle' | 'lemonsqueezy' | null;

export interface SubscriptionInfo {
  hasActiveSubscription: boolean;
  tier: TierName;
  platform: SubscriptionPlatform;
  status: 'active' | 'canceled' | 'expired' | 'paused' | null;
  expiresAt: string | null;
  autoRenewing: boolean;
  willExpire?: boolean; // true if canceled but still active
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  subscriptionActive: boolean; // true → Has subscription, false → Free
  tier?: TierName; // free | basic | pro
}

interface UserState {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;

  /** @deprecated Use userTier instead for proper tier-based access control */
  isPremium: boolean;
  /** User's subscription tier: free, basic, or pro */
  userTier: TierName;
  /** Subscription details including platform */
  subscription: SubscriptionInfo | null;

  // Internal state for deduplication
  _profileFetching: boolean;
  _subscriptionFetching: boolean;
  _lastProfileFetch: number | null;
  _lastSubscriptionFetch: number | null;

  fetchProfile: () => Promise<void>;
  fetchSubscription: () => Promise<void>;

  /** @deprecated Use setUserTier instead */
  setIsPremium: (value: boolean) => void;
  setUserTier: (tier: TierName) => void;
}

// Cache duration: 30 seconds for user data
const CACHE_DURATION = 30 * 1000;

export const useUserStore = create<UserState>((set, get) => ({
  profile: null,
  loading: false,
  error: null,
  isPremium: false,
  userTier: 'free',
  subscription: null,
  _profileFetching: false,
  _subscriptionFetching: false,
  _lastProfileFetch: null,
  _lastSubscriptionFetch: null,

  fetchProfile: async () => {
    const token = storage.getJwtToken();
    if (!token) {
      set({ profile: null, isPremium: false, userTier: 'free', subscription: null });
      return;
    }

    const { _profileFetching, _lastProfileFetch, profile } = get();

    // Skip if already fetching
    if (_profileFetching) {
      return;
    }

    // Skip if cache is still valid and we have profile
    if (profile && _lastProfileFetch && Date.now() - _lastProfileFetch < CACHE_DURATION) {
      return;
    }

    set({ loading: true, error: null, _profileFetching: true });

    try {
      const res = await axios.get<UserProfile>(`${baseUrl}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profileData = res.data;
      const tier = profileData.tier || (profileData.subscriptionActive ? 'basic' : 'free');
      set({
        profile: profileData,
        isPremium: profileData.subscriptionActive, // Keep for backward compatibility
        userTier: tier,
        loading: false,
        _profileFetching: false,
        _lastProfileFetch: Date.now(),
      });

      // Also fetch subscription details
      get().fetchSubscription();
    } catch (err: any) {
      // Check if it's a 401 Unauthorized error
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        // Clear invalid token
        storage.clear();
        // Redirect to login page
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/get-started')) {
          window.location.href = '/get-started';
        }
      }

      set({
        error: isUnauthorized ? "Session expired. Please login again." : "Không lấy được thông tin người dùng",
        loading: false,
        profile: null,
        isPremium: false,
        userTier: 'free',
        subscription: null,
        _profileFetching: false,
      });
    }
  },

  fetchSubscription: async () => {
    const token = storage.getJwtToken();
    if (!token) {
      set({ subscription: null });
      return;
    }

    const { _subscriptionFetching, _lastSubscriptionFetch, subscription } = get();

    // Skip if already fetching
    if (_subscriptionFetching) {
      return;
    }

    // Skip if cache is still valid and we have subscription data
    if (subscription && _lastSubscriptionFetch && Date.now() - _lastSubscriptionFetch < CACHE_DURATION) {
      return;
    }

    set({ _subscriptionFetching: true });

    try {
      const res = await axios.get<{ data: SubscriptionInfo; success: boolean }>(
        `${baseUrl}/subscription/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        const sub = res.data.data;
        set({
          subscription: sub,
          userTier: sub.tier,
          isPremium: sub.hasActiveSubscription,
          _subscriptionFetching: false,
          _lastSubscriptionFetch: Date.now(),
        });
      } else {
        set({ _subscriptionFetching: false });
      }
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      // Don't clear subscription on error, keep existing state
      set({ _subscriptionFetching: false });
    }
  },

  setIsPremium: (value: boolean) => {
    const cur = get().profile;
    set({
      isPremium: value,
      userTier: value ? 'basic' : 'free', // Sync with isPremium for backward compatibility
      profile: cur ? { ...cur, subscriptionActive: value } : null,
    });
  },

  setUserTier: (tier: TierName) => {
    const cur = get().profile;
    set({
      userTier: tier,
      isPremium: tier !== 'free', // Sync with isPremium for backward compatibility
      profile: cur ? { ...cur, tier, subscriptionActive: tier !== 'free' } : null,
    });
  },
}));
